import { actions as boardSettingActions } from "app/containers/Features/containers/Boards/components/Settings/redux/slice";
import { chunkArray } from "app/containers/Features/containers/Boards/helpers";
import { Board } from "app/containers/Features/containers/Boards/types";
import { actions as notificationActions } from "app/containers/Features/containers/Notification/redux/slice";
import {
  selectBoardMenuData,
  selectUserFormData,
  selectWorkSpaceDetails,
} from "app/redux/selector";
import { actions } from "app/redux/slice";
import {
  ArchiveType,
  BoardTemplateEnum,
  IBoardFixedTemp,
  IBoardMenu,
  ITeamSettings,
  IUserData,
  IWorkSpaceDetails,
  IWorkspaceTeams,
} from "app/types";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { API_URL, TICKET_API_URL } from "utils/constants";
import { handleApiError } from "utils/errorHandler";
import { request } from "utils/request";
import toastService from "utils/toast";

// user my profile request
export function* doGetMyProfileRequest() {
  try {
    yield delay(500);
    const requestURL = `${API_URL}auth/me`;
    const options = {
      method: "GET",
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      handleApiError(err);
      return;
    }
    const userData = data?.data;
    yield put(actions.setUserFormData(userData));
    yield put(notificationActions.setMuteState(userData?.mutedTill ?? null));
  } catch (err: any) {
    handleApiError(err);
  }
}
export function* doGetUserWorkspaceRequest(action: {
  payload: { callback: () => void };
}) {
  yield delay(500);
  try {
    const requestURL = `${API_URL}auth/workspace`;
    const options = {
      method: "GET",
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      handleApiError(err);
      return;
    }

    yield put(actions.setUserWorkspaceData(data?.data));
  } catch (err: any) {
    handleApiError(err);
  }
}
export function* addSelectedTeamToWorkspaceRequest(action: {
  payload: {
    obj: {
      teamId: string;
      projectId: string;
    };
  };
}) {
  try {
    yield delay(500);
    const payload = action.payload.obj;
    // fetch user basic details
    const userForm: IUserData = yield select(selectUserFormData);
    const requestURL = `${API_URL}auth/profile/context/select`;
    const options = {
      method: "POST",
      body: JSON.stringify(payload),
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      handleApiError(err);
      return;
    }
    // update local team and project ids into the user workspace
    yield put(
      actions.updateUserFormData({
        ...userForm,
        selectedProjectId: payload.projectId,
        selectedTeamId: payload.teamId,
      }),
    );
  } catch (err) {
    handleApiError(err);
  }
}
export function* fetchTeamSettingRequest(action: {
  payload: { data: string[] };
}) {
  yield delay(500);

  try {
    yield put(actions.setIsBoardLoading(true));

    const requestURL = `${TICKET_API_URL}team/settings`;

    const teamIds = action.payload.data;
    const chunks = chunkArray(teamIds, 5);

    let allData: ITeamSettings[] = [];

    for (const chunk of chunks) {
      const options = {
        method: "POST",
        body: JSON.stringify(chunk),
      };

      const { data, err } = yield call(request, requestURL, options);

      if (err || !data) {
        handleApiError(err);
        continue;
      }
      allData = [...allData, ...(data?.data || [])];
    }

    yield put(actions.setTeamSetting(allData));
  } catch (err: any) {
    handleApiError(err);
  } finally {
    yield put(actions.setIsBoardLoading(false));
  }
}
export function* fetchProjectSettingsRequest(action: { payload: string }) {
  yield delay(500);
  try {
    const projectId = action.payload;
    const requestURL = `${TICKET_API_URL}project/settings/${projectId}`;
    const options = {
      method: "GET",
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      handleApiError(err);
      return;
    }
    const rData = data.data;
    yield put(actions.setProjectSettingsDetails(rData));
  } catch (err: any) {
    handleApiError(err);
  }
}

export function* doCreateFixTempBoardRequest(action: {
  payload: {
    data: IBoardFixedTemp;
    callback: (data: Board) => void;
  };
}) {
  yield delay(500);
  try {
    yield put(actions.setIsBoardCreate(true));
    const requestURL = `${TICKET_API_URL}board/template/create`;
    const options = {
      method: "POST",
      body: JSON.stringify(action.payload.data),
    };
    const { data, err } = yield call(request, requestURL, options);
    yield put(actions.setIsBoardCreate(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    toastService.success(data?.message);
    yield put(boardSettingActions.setBoardFormData(data?.data));
    yield put(boardSettingActions.setDraftBoardFormData(data?.data));
    yield call(action.payload.callback, data.data);
  } catch (err: any) {
    yield put(actions.setIsBoardCreate(false));
    handleApiError(err);
  }
}

export function* updateBoardOrderRequest() {
  yield delay(500);
  try {
    yield put(actions.setIsBoardCreate(true));

    const boards: IBoardMenu[] = yield select(selectBoardMenuData);
    const board = {
      boards: boards.map((item) => {
        return { _id: item._id, order: item.order };
      }),
    };
    const requestURL = `${TICKET_API_URL}board/update/order`;
    const options = {
      method: "POST",
      body: JSON.stringify(board),
    };

    const { data, err } = yield call(request, requestURL, options);
    yield put(actions.setIsBoardCreate(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
  } catch (err: any) {
    yield put(actions.setIsBoardCreate(false));
    handleApiError(err);
  }
}

export function* onLogout() {
  yield delay(0);
  localStorage.removeItem("accessToken");
  localStorage.removeItem("projectId");
}
export function* doGetBoardsMenuRequest(action: { payload: { id: string } }) {
  yield delay(500);
  try {
    yield put(actions.setIsBoardMenuLoading(true));
    const { id } = action.payload;
    const requestURL = `${TICKET_API_URL}board/list/${id}`;
    const options = {
      method: "GET",
    };
    const { data, err } = yield call(request, requestURL, options);
    if (err || !data) {
      handleApiError(err);
      return;
    }
    yield put(actions.setIsBoardMenuLoading(false));
    yield put(actions.setBoardMenuData(data?.data));
  } catch (err: any) {
    yield put(actions.setIsBoardMenuLoading(false));
    handleApiError(err);
  }
}

export function* doArchiveRequest(action: {
  payload: {
    projectId: string;
    dataType: ArchiveType;
    dataId: string[];
    callback: () => void;
  };
}) {
  yield delay(500);
  try {
    const { projectId } = action.payload;
    const { dataType } = action.payload;
    const { dataId } = action.payload;
    const formData = {
      projectId: projectId,
      dataType: dataType,
      ids: dataId,
    };
    yield put(actions.setIsArchiveLoading(true));
    const requestURL = `${TICKET_API_URL}archive/create`;
    const options = {
      method: "POST",
      body: JSON.stringify(formData),
    };
    const { data, err } = yield call(request, requestURL, options);
    yield put(actions.setIsArchiveLoading(false));
    if (err || !data) {
      handleApiError(err);
      return;
    }
    toastService.success(data.message);
    yield call(action.payload.callback);
  } catch (err: any) {
    yield put(actions.setIsArchiveLoading(false));
    handleApiError(err);
  }
}

export function* fetchSelectedTeamRequest(action: { payload: string }) {
  yield delay(500);
  try {
    const id = action.payload;

    yield put(actions.setSelectedTeamLoading(true));

    const workSpaceData: IWorkSpaceDetails = yield select(
      selectWorkSpaceDetails,
    );
    const selectedTeamData = workSpaceData?.teams?.find(
      (team) => team._id === id,
    );
    yield put(actions.setSelectedTeamLoading(false));
    yield put(actions.setSelectedTeam(selectedTeamData as IWorkspaceTeams));
  } catch (err: any) {
    yield put(actions.setSelectedTeamLoading(false));
    handleApiError(err);
  }
}

export default function* appSaga() {
  // user my profile request
  yield takeLatest(actions.doGetMyProfile, doGetMyProfileRequest);
  // user initial workspace like teams and project
  yield takeLatest(actions.doGetUserWorkspace, doGetUserWorkspaceRequest);
  // get ticket setting teams and project
  yield takeLatest(actions.fetchTeamsSettings, fetchTeamSettingRequest);
  // create board via tamp
  yield takeLatest(actions.doCreateFixTempBoard, doCreateFixTempBoardRequest);
  // update board order
  yield takeLatest(actions.doUpdateBoardOrder, updateBoardOrderRequest);
  // update new board order
  yield takeLatest(actions.updateNewBoardOrderState, updateBoardOrderRequest);
  //board menu request
  yield takeLatest(actions.doGetBoardsMenu, doGetBoardsMenuRequest);
  // project settings details
  yield takeLatest(actions.fetchProjectSettings, fetchProjectSettingsRequest);
  //archive request
  yield takeLatest(actions.doArchive, doArchiveRequest);

  yield takeLatest(actions.onLogout, onLogout);
  yield takeLatest(actions.fetchSelectedTeam, fetchSelectedTeamRequest);
  // fetch project details from workspace setting data
  yield takeLatest(
    actions.addSelectedTeamToWorkspace,
    addSelectedTeamToWorkspaceRequest,
  );
}
